/**
 * Перемикання активного клієнта (tenant) — ЄДИНИЙ шлях для перемикача
 * клієнтів і для вибору організації іншого клієнта у шапці (ч.1068).
 *
 * Що робить:
 *  1. (опц.) записує організацію за замовчуванням для НОВОГО клієнта;
 *  2. записує нового клієнта для НАСТУПНОГО завантаження сторінки — стан
 *     живої сторінки не змінюється до її вивантаження (інакше відкладені
 *     записи чернеток/вкладок лягли б під ключ нового клієнта, а повторні
 *     запити пішли б з його X-Tenant-Id — див. setActiveTenantOnNextLoad);
 *  3. ч.1197 (рішення власника): закриває ВСІ відкриті вкладки і стирає
 *     незбережені чернетки клієнта, якого покидаємо, — у новому клієнті робота
 *     починається з чистого аркуша. Попередження користувачу показує
 *     `useSwitchTenant` (ЄДИНИЙ спосіб викликати перемикання з інтерфейсу);
 *  4. повністю перезавантажує сторінку (перечитування /auth/me, прав, меню;
 *     кеш запитів живе лише в памʼяті сторінки і зникає разом з нею), але на
 *     адресі БЕЗ записів попереднього клієнта: документ чи картка іншого
 *     клієнта в новому однаково «не знайдено», тому з `/goods-sales/<id>` і з
 *     `/goods-sales/new` переходимо у список `/goods-sales`; параметри адреси
 *     (`?open=<id>`, `?org=<id>`…) теж відкидаємо.
 *
 * Безпека не залежить від цього коду: доступ до клієнта і його організацій
 * бекенд перевіряє на кожному запиті (membership + RLS).
 */
import { setActiveTenantOnNextLoad } from '@/auth/store';
import { setDefaultOrgForTenant } from '@/store/defaults';
import { discardAllDrafts } from '@/store/formDrafts';
import { resetTabsForTenantSwitch } from '@/store/openTabs';
import { markPageLeaving } from '@/store/pageLeaving';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Адреса, безпечна для відкриття в іншого клієнта: обрізана до першого id
 * запису. ч.1197: «новий документ/картка» (`…/new`) — теж до списку розділу:
 * вкладки закрито, порожня форма нового документа одразу стала б вкладкою.
 */
export function pathForOtherTenant(pathname: string): string {
  const segs = pathname.split('/').filter(Boolean);
  const i = segs.findIndex((s) => UUID_RE.test(s) || s === 'new');
  return `/${(i >= 0 ? segs.slice(0, i) : segs).join('/')}`;
}

export function switchTenant(
  tenantId: string,
  opts: { defaultOrgId?: string; go?: (url: string) => void } = {},
): void {
  if (opts.defaultOrgId) setDefaultOrgForTenant(tenantId, opts.defaultOrgId);
  // ч.1197: вкладки і чернетки — геть; після позначки «сторінка йде» сховища
  // вже нічого не запишуть (відкладений запис чернетки не воскресить скинуте).
  resetTabsForTenantSwitch(tenantId);
  discardAllDrafts();
  markPageLeaving();
  setActiveTenantOnNextLoad(tenantId);
  const url = pathForOtherTenant(window.location.pathname);
  (opts.go ?? ((u: string) => window.location.assign(u)))(url);
}
